import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { ProductStatus } from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class ProductStatusService {
	private readonly BASE_PATH = 'product-status';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public productStatusBs$: BehaviorSubject<ProductStatus[]> = new BehaviorSubject<ProductStatus[]>([]);
	public productStatus$: Observable<ProductStatus[]> = this.productStatusBs$.pipe(
		filter(productStatuses => productStatuses !== undefined && productStatuses.length > 0)
	);

	constructor(private api: ApiService) {
		this.getAll$();
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public getByUuid(uuid: string): ProductStatus | undefined {
		return this.productStatusBs$.getValue().find(ps => ps.uuid === uuid);
	}

	public getByProductName(productName: string): ProductStatus[] | undefined {
		return this.productStatusBs$.getValue().filter(ps => ps.product_name === productName);
	}

	public async getProductStatusByProductName$(productName: string): Promise<ProductStatus[]> {
		return this.withLoading(async () => {
			const productStatuses = await firstValueFrom(
				this.api.get<ProductStatus[]>(this.BASE_PATH + '/get-by-product/' + productName)
			);
			return productStatuses;
		});
	}

	public async getAll$(): Promise<ProductStatus[]> {
		return this.withLoading(async () => {
			const productStatuses = await firstValueFrom(this.api.get<ProductStatus[]>(this.BASE_PATH + '/get-all'));
			this.productStatusBs$.next(productStatuses);
			return productStatuses;
		});
	}

	public async create$(productStatus: ProductStatus): Promise<ProductStatus> {
		return this.withLoading(async () => {
			const createdProductStatus = await firstValueFrom(
				this.api.post<ProductStatus>(this.BASE_PATH, productStatus)
			);
			this.productStatusBs$.next([...this.productStatusBs$.getValue(), createdProductStatus]);
			return createdProductStatus;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(this.BASE_PATH + '/' + uuid));
			this.productStatusBs$.next(this.productStatusBs$.getValue().filter(ps => ps.uuid !== uuid));
		});
	}

	public async update$(uuid: string, productStatus: ProductStatus): Promise<ProductStatus> {
		return this.withLoading(async () => {
			const updatedProductStatus = await firstValueFrom(
				this.api.put<ProductStatus>(this.BASE_PATH + '/' + uuid, productStatus)
			);
			const productStatuses = this.productStatusBs$
				.getValue()
				.map(ps => (ps.uuid === uuid ? updatedProductStatus : ps));
			this.productStatusBs$.next(productStatuses);
			return updatedProductStatus;
		});
	}

	public async moveUp$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.post<void>(this.BASE_PATH + '/move-up/' + uuid, {}));
			await this.getAll$();
		});
	}

	public async moveDown$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.post<void>(this.BASE_PATH + '/move-down/' + uuid, {}));
			await this.getAll$();
		});
	}
}
